/**
 * Tracks for drawing only: each track's centre and box size smoothed over time and filled in on every frame, so a
 * dot or box glides with the object instead of stepping every `step` frames (the general search reads every 3rd
 * frame) and wobbling with the detector. Counting keeps the tracks as they are (tracksModel.indexTracks).
 *
 * Each frame's value is a local linear fit of the track's samples around it, weighted by a gaussian of SMOOTH_SEC:
 * a moving object is neither dragged behind nor rounded off at the ends of its track, as an average would. Samples
 * more than MAX_GAP_SEC apart are not bridged (the object was not seen in between), nor a jump of more than
 * JUMP_BODIES body lengths between two samples (a cut, or the tracker hopping): the dot jumps there, it does not
 * slide across the picture.
 */

export const SMOOTH_SEC = 0.2;
export const MAX_GAP_SEC = 0.6;
export const JUMP_BODIES = 1.5;

const KEYS = ['x', 'y', 'w', 'h'];

// gaussian weights of sigma frames by frame distance, out to 3 sigma: frames are whole numbers, so a table
const weights = sigma => {
	const reach = Math.floor(3 * sigma);
	const w = new Float64Array(2 * reach + 1);
	for (let d = -reach; d <= reach; d += 1) w[d + reach] = Math.exp(-0.5 * (d / sigma) ** 2);
	return { w, reach, sigma };
};

// local linear fit at frame f of the samples a..b (indices into t / values), written into out; v0 / v1 are scratch
const fitAt = (t, values, a, b, f, g, out, v0, v1) => {
	const Q = values.length;
	let s0 = 0;
	let s1 = 0;
	let s2 = 0;
	v0.fill(0);
	v1.fill(0);
	for (let k = a; k <= b; k += 1) {
		const dt = t[k] - f;
		if (dt < -g.reach) continue;
		if (dt > g.reach) break;
		const w = Number.isInteger(dt) ? g.w[dt + g.reach] : Math.exp(-0.5 * (dt / g.sigma) ** 2);
		s0 += w;
		s1 += w * dt;
		s2 += w * dt * dt;
		for (let q = 0; q < Q; q += 1) {
			const v = values[q][k];
			v0[q] += w * v;
			v1[q] += w * dt * v;
		}
	}
	const det = s0 * s2 - s1 * s1;
	const linear = det > 1e-9 * s0 * s0;
	for (let q = 0; q < Q; q += 1) out[q] = linear ? (s2 * v0[q] - s1 * v1[q]) / det : v0[q] / s0;
	return out;
};

// whether the track moves more than JUMP_BODIES body lengths from sample k to the next
const jumps = (tr, k) =>
	Math.hypot(tr.x[k + 1] - tr.x[k], tr.y[k + 1] - tr.y[k]) >
	JUMP_BODIES * Math.max(1, Math.sqrt(tr.w[k] * tr.h[k]));

/** One track with its points on every frame between its samples (and `hold` frames after each run), smoothed. */
export const smoothTrack = (track, { fps = 30, step = 1, hold = 1 } = {}) => {
	const t = track.t || [];
	if (!t.length) return track;
	const sigma = Math.max(step, SMOOTH_SEC * fps);
	const maxGap = Math.max(2 * step, Math.round(MAX_GAP_SEC * fps));
	const values = KEYS.map(k => track[k]);
	const g = weights(sigma);
	const fit = new Float64Array(KEYS.length);
	const v0 = new Float64Array(KEYS.length);
	const v1 = new Float64Array(KEYS.length);
	const out = { t: [], x: [], y: [], w: [], h: [] };
	let a = 0;
	while (a < t.length) {
		let b = a;
		while (b + 1 < t.length && t[b + 1] - t[b] <= maxGap && !jumps(track, b)) b += 1;
		const end = t[b] + Math.max(0, hold - 1);
		// frames only grow, so the first sample within reach only moves on: each fit reads the few samples near its
		// frame, not the run from its start (that made a long video's tracks take seconds on the main thread)
		let lo = a;
		for (let f = t[a]; f <= end; f += 1) {
			const at = Math.min(f, t[b]);
			while (lo < b && t[lo] - at < -g.reach) lo += 1;
			const v = fitAt(t, values, lo, b, at, g, fit, v0, v1);
			out.t.push(f);
			KEYS.forEach((k, q) => out[k].push(k === 'w' || k === 'h' ? Math.max(1, v[q]) : v[q]));
		}
		a = b + 1;
	}
	return { ...track, ...out };
};

// a track smoothed once: the sensitivity and size sliders and the corrections poll only change which tracks are
// drawn (the same track objects), so a slider tick re-indexes instead of smoothing every track again
const smoothed = new WeakMap();
const smoothOnce = (tr, fps, step) => {
	const key = `${fps}/${step}`;
	const hit = smoothed.get(tr);
	if (hit?.key === key) return hit.out;
	const out = smoothTrack(tr, { fps, step, hold: step });
	smoothed.set(tr, { key, out });
	return out;
};

/** The document with every track smoothed for drawing; `step` becomes 1 (every frame has its point). */
export const smoothForDrawing = doc => {
	if (!doc?.tracks) return doc;
	const fps = doc.fps || 30;
	const step = Math.max(1, doc.step | 0);
	return { ...doc, step: 1, tracks: doc.tracks.map(tr => smoothOnce(tr, fps, step)) };
};
